
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from "@/components/ui/label";
import { Button } from '@/components/ui/button';
import { UpdateFormData } from './types';
import { Loader2 } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface UpdateFormProps {
  formData: UpdateFormData;
  setFormData: React.Dispatch<React.SetStateAction<UpdateFormData>>;
  onSubmit: (e: React.FormEvent) => void;
  editingId: number | null;
  isLoading: boolean;
  uniqueCountries: string[];
  availableLeagues: string[];
  availableYears: string[];
}

export const UpdateForm: React.FC<UpdateFormProps> = ({
  formData,
  setFormData,
  onSubmit,
  editingId,
  isLoading,
  uniqueCountries,
  availableLeagues,
  availableYears
}) => {
  const updateFormData = (field: keyof UpdateFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const frequencies = [
    { value: 'hourly', label: 'Every Hour' },
    { value: 'daily', label: 'Once a Day' },
    { value: 'weekly', label: 'Once a Week' },
    { value: 'monthly', label: 'Once a Month' }
  ];

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="country">Country</Label>
        <Select 
          value={formData.country} 
          onValueChange={(value) => updateFormData('country', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a country" />
          </SelectTrigger>
          <SelectContent>
            {uniqueCountries.map(country => (
              <SelectItem key={country} value={country}>
                {country}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="league">League</Label>
        <Select 
          value={formData.league} 
          onValueChange={(value) => updateFormData('league', value)}
          disabled={!formData.country}
        >
          <SelectTrigger>
            <SelectValue placeholder={!formData.country ? "Select a country first" : "Select a league"} />
          </SelectTrigger>
          <SelectContent>
            {availableLeagues.map(league => (
              <SelectItem key={league} value={league}>
                {league}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="season">Season</Label>
        <Select 
          value={formData.season} 
          onValueChange={(value) => updateFormData('season', value)}
          disabled={!formData.league}
        >
          <SelectTrigger>
            <SelectValue placeholder={!formData.league ? "Select a league first" : "Select a season"} />
          </SelectTrigger>
          <SelectContent>
            {availableYears.map(year => (
              <SelectItem key={year} value={year}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="excel_url">Excel URL</Label>
        <Input
          id="excel_url"
          value={formData.excel_url}
          onChange={(e) => updateFormData('excel_url', e.target.value)}
          placeholder="https://example.com/data.xlsx"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="update_frequency">Update Frequency</Label>
        <Select 
          value={formData.update_frequency} 
          onValueChange={(value) => updateFormData('update_frequency', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select update frequency" />
          </SelectTrigger>
          <SelectContent>
            {frequencies.map(freq => (
              <SelectItem key={freq.value} value={freq.value}>
                {freq.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="admin_email">Notification Email</Label>
        <Input
          id="admin_email"
          type="email"
          value={formData.admin_email || ''}
          onChange={(e) => updateFormData('admin_email', e.target.value)}
          placeholder="admin@example.com"
        />
        <p className="text-xs text-muted-foreground">
          Email to receive update notifications
        </p>
      </div>

      <Button 
        type="submit" 
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {editingId ? 'Updating...' : 'Adding...'}
          </>
        ) : (
          editingId ? 'Update Configuration' : 'Add Configuration'
        )}
      </Button>
    </form>
  );
};
